/**
 * The stores desk.
 *
 * Four questions, in the order somebody actually asks them:
 *
 *   1. What is stopping a job today?      — jobs waiting on a part
 *   2. What must be bought?               — the replenishment run
 *   3. What is coming, and is it late?    — purchasing
 *   4. What have we got?                  — the catalogue
 *
 * The catalogue is last on purpose. Every stores system in the world opens on
 * the list of everything it holds, which is the one view that answers none of
 * the three questions above. The list is still there, in full, and it is still
 * the thing most people scroll to — but it does not get to be the headline.
 */
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  arrivesInTime,
  categoryLabel,
  NO_SUPPLIER,
  STATE_LABEL,
  STORE_CATEGORIES,
  daysUntil,
  type StockRow,
  type StoresRegister,
} from "../../domain/stores";
import { answerStores, buildStoresDesk, materialForStores, STORES_MODEL_PROMPT } from "../../domain/storesDesk";
import { raiseReplenishment, type Result } from "../../domain/storesEdit";
import { Button, Card, EmptyState, Field, Select, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { AskCard, Refs, Tiles, x as ex } from "../executive/shared";
import { useScreenContext } from "../../assistant/screen";
import { PurchasingCard } from "./Purchasing";
import { DeliveryDialog, ExternalOrderDialog, IntakeDialog, NewLineDialog } from "./StoresIntake";
import { CategoriesCard } from "./StoresCategories";
import { t, tn } from "../../i18n";
import { STORES_TOPIC, useStores } from "./useStores";
import s from "./Stores.module.css";

/* named here as well, for everything that has always imported it from the desk */
export { STORES_TOPIC };

export function StoresPage() {
  const session = useSession();
  /*
   * The desk is rendered at /logistics as part of Store & Warehouse, and at
   * /stores for anything that still links there. The screen context must name
   * the route it is actually on, or DeskView compares it with the pathname,
   * finds no match, and the desk loses both its summary and Sky's context.
   */
  const { pathname } = useLocation();
  /*
   * Held by the platform's stores register where it holds one for this site
   * (routers/stores.py), and otherwise, as it always was, under its own note
   * topic, attributed and dated like the equipment register and the finance
   * figures. The note was shaped so that the day the platform held stores this
   * became a read and the screen above it did not change; useStores.ts is that
   * read, and the screen did not.
   */
  const entered = useStores();
  const register = entered.value;
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [receiving, setReceiving] = useState<string | null>(null);
  /* the four ways a real store gets into the register in the first place */
  const [intake, setIntake] = useState<"load" | "line" | "direct" | "external" | null>(null);

  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("all");
  const [only, setOnly] = useState<"all" | "attention" | "critical" | "reserved" | "obsolete">("all");

  const desk = useMemo(
    () => buildStoresDesk({ now: new Date(), siteCode: session.site.code, register }),
    [register, session.site.code],
  );

  const shown = useMemo(
    () =>
      desk.rows.filter(
        (r) =>
          (category === "all" || r.item.category === category) &&
          (location === "all" || r.item.bins.some((b) => b.location === location)) &&
          (only === "all" ||
            (only === "attention" && r.state !== "ok" && r.state !== "over_max") ||
            (only === "critical" && Boolean(r.item.critical)) ||
            (only === "reserved" && r.reserved > 0) ||
            (only === "obsolete" && r.obsolete)),
      ),
    [desk.rows, category, location, only],
  );

  const apply = async (result: Result) => {
    if (!result.ok) {
      setRefused(result.why);
      setDone(null);
      return;
    }
    setRefused(null);
    setDone(result.note ?? null);
    /* the platform, when it holds the register, has the last word */
    const no = await entered.commit(result);
    if (no) {
      setRefused(no);
      setDone(null);
    }
  };

  const bringAcross = async () => {
    setImporting(true);
    const said = await entered.importNote();
    setImporting(false);
    setRefused(said.ok ? null : said.text);
    setDone(said.ok ? said.text : null);
  };

  useScreenContext({
    route: pathname,
    title: "Store & Warehouse",
    /*
     * Worked at, not read. Everything on this desk is an action a storekeeper
     * takes — load the stock, book a delivery in, hand parts over the counter
     * — and behind a summary they are invisible.
     */
    worksInFull: true,
    summary: desk.boardLine,
    material: materialForStores(desk),
    figures: desk.figures,
    own: (q) => answerStores(q, desk),
    prompt: STORES_MODEL_PROMPT,
    offers: [
      { label: "What is out of stock?", ask: "what is out of stock?" },
      { label: "What is stopping a job?", ask: "which jobs are waiting on a part?" },
      { label: "What should we buy?", ask: "what is below the reorder point?" },
      { label: "What is late?", ask: "which deliveries are overdue?" },
    ],
  });

  return (
    <Page
      eyebrow={t("{site} · Store & Warehouse", { site: session.site.code })}
      title="Store & Warehouse"
      actions={
        <div className={s.actions}>
          <Button variant="primary" data-testid="stores-load" onClick={() => setIntake("load")}>
            {t("Load the stock")}
          </Button>
          <Button data-testid="stores-add-line" onClick={() => setIntake("line")}>
            {t("Add a line")}
          </Button>
          <Button data-testid="stores-direct" onClick={() => setIntake("direct")}>
            {t("Book in a delivery")}
          </Button>
          <Button data-testid="stores-external" onClick={() => setIntake("external")}>
            {t("Record an order")}
          </Button>
          <Button
            data-testid="stores-replenish"
            disabled={desk.toRequest === 0}
            onClick={() => void apply(raiseReplenishment(register, desk.run, { name: session.principal.name }))}
          >
            {desk.toRequest === 0
              ? t("Everything short is already asked for")
              : tn(desk.toRequest, "Raise the replenishment ({n} line)", "Raise the replenishment ({n} lines)")}
          </Button>
          <Link to="/stores/counter" className={ex.tag} data-testid="stores-counter-link">
            {t("The counter")}
          </Link>
          <Link to="/stores/inventory" className={ex.tag} data-testid="stores-inventory-link">
            {t("The whole inventory")}
          </Link>
          <button type="button" className={ex.tag} onClick={() => window.print()}>
            {t("Print")}
          </button>
        </div>
      }
    >
      <p className={s.lede}>
        {t(
          "What's on the shelf, promised to jobs, on order, or running out. On hand, reserved and available are kept separate.",
        )}
      </p>
      {entered.mode === "platform" ? (
        <p className={s.note} data-testid="stores-basis" data-held="platform">
          {t(
            "Kept in the platform's stores register. Stock can't go below zero, and two people can't take the same last part. Parts taken without being booked out don't show in any figure.",
          )}
        </p>
      ) : (
        <p className={s.note} data-testid="stores-basis">
          {t(
            "Entered by hand, not read from a stores system, so nothing here reorders or raises alerts on its own. Parts taken without being booked out don't show in any figure.",
          )}
        </p>
      )}
      {entered.noteWaiting && (
        <div className={s.note} data-testid="stores-import">
          <p>
            {t(
              "The platform now has its own stores register. Move this one across once, and all lines, movements, reservations, requests and orders go with it.",
            )}
          </p>
          <Button variant="primary" disabled={importing} onClick={() => void bringAcross()} data-testid="stores-import-go">
            {importing ? t("Bringing it across…") : t("Bring it onto the platform")}
          </Button>
        </div>
      )}

      <Tiles items={desk.tiles} currency={register.currency} />
      {refused && (
        <p className={s.refused} role="alert" data-testid="stores-refused">
          {refused}
        </p>
      )}
      {done && <p className={s.saved}>{done}</p>}
      {entered.savedTo && <p className={ex.saved}>{entered.savedTo}</p>}

      {/* 1 — what is stopping a job */}
      <Card
        collapsible
        title="Jobs waiting on a part"
        sub="What each job is owed, and if it's coming"
      >
        {desk.waiting.length === 0 ? (
          <EmptyState>{t("No open job is short of a part it has reserved.")}</EmptyState>
        ) : (
          <ul className={s.jobList} data-testid="stores-waiting">
            {desk.waiting.map((w) => (
              <li key={w.workOrder} className={s.job} data-blocked={w.blocked}>
                <span className={s.jobNo}>{w.workOrder}</span>
                <span className={s.jobWhy}>
                  {tn(w.short, "{n} part short", "{n} parts short")} — {w.sentence}
                </span>
                {w.blocked && (
                  <span className={s.bad}>
                    {t("Nothing on order: this job is stopped")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* 2 — what must be bought */}
      <Card
        collapsible
        title="What must be bought"
        sub="Grouped by supplier: one order each"
      >
        {desk.run.length === 0 ? (
          <EmptyState>{t("Nothing is below its reorder point.")}</EmptyState>
        ) : (
          <>
            {desk.late.length > 0 && (
              <p className={s.note} data-testid="stores-late">
                {tn(
                  desk.late.length,
                  "{n} line cannot arrive before its shelf empties, at the lead time recorded for it. The reorder point itself is too low, not merely the stock.",
                  "{n} lines cannot arrive before their shelves empty, at the lead times recorded for them. Their reorder points are too low, not merely their stock.",
                )}
              </p>
            )}
            {desk.run.map((g) => (
              <div key={g.supplier} className={s.group}>
                <div className={s.groupHead}>
                  <span className={s.groupName}>{g.supplier === NO_SUPPLIER ? t(NO_SUPPLIER) : g.supplier}</span>
                  <span className={s.groupTotal}>
                    {g.total === null
                      ? t("total unknown — a line carries no cost")
                      : `${Math.round(g.total).toLocaleString()} ${register.currency}`}
                  </span>
                </div>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("Line")}</th>
                      <th>{t("State")}</th>
                      <th className={s.num}>{t("To buy")}</th>
                      <th className={s.num}>{t("Cover left")}</th>
                      <th className={s.num}>{t("Lead time")}</th>
                      <th>{t("Arrives in time?")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.lines.map((l) => {
                      const late = arrivesInTime(l);
                      return (
                        <tr key={l.item.sku}>
                          <td>
                            <Link to={`/stores/${encodeURIComponent(l.item.sku)}`}>{l.item.name}</Link>
                            <div className={s.sku}>{l.item.sku}</div>
                          </td>
                          <td>
                            <span className={s.state} data-state={l.state}>
                              {t(STATE_LABEL[l.state])}
                            </span>
                          </td>
                          <td className={s.num}>
                            {`${l.qty} ${l.item.uom}`}
                            {l.requested && (
                              /* the click has to change the table it was made above */
                              <div className={s.asked} data-testid={`asked-${l.item.sku}`}>
                                {t("asked for on {n}", { n: l.requested })}
                              </div>
                            )}
                          </td>
                          <td className={s.num}>
                            {l.emptyInDays === null ? (
                              <span className={s.unknown}>{t("unknown")}</span>
                            ) : (
                              tn(l.emptyInDays, "{n} day", "{n} days")
                            )}
                          </td>
                          <td className={s.num}>
                            {l.item.leadDays === undefined ? (
                              <span className={s.unknown}>{t("not recorded")}</span>
                            ) : (
                              tn(l.item.leadDays, "{n} day", "{n} days")
                            )}
                          </td>
                          <td>
                            {late.inTime === null ? (
                              <span className={s.unknown}>{t("cannot say")}</span>
                            ) : late.inTime ? (
                              <span className={s.good}>{t("yes")}</span>
                            ) : (
                              <span className={s.late}>
                                {tn(late.shortBy ?? 0, "{n} day too late", "{n} days too late")}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ))}
          </>
        )}
      </Card>

      {/* 3 — what is coming */}
      <PurchasingCard
        desk={desk}
        register={register}
        who={session.principal.name}
        onApply={apply}
        onReceive={setReceiving}
      />

      {/* 4 — what we hold */}
      <Card
        collapsible title="The catalog" sub="Worst first, then soonest to run out">
        <div className={s.chips}>
          {(
            [
              ["all", t("Everything")],
              ["attention", t("Needs attention")],
              ["critical", t("Critical spares")],
              ["reserved", t("Promised to a job")],
              ["obsolete", t("Not moved in a year")],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={s.chip}
              aria-pressed={only === key}
              onClick={() => setOnly(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={s.filters}>
          <Field label={t("Category")}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label={t("Category")}>
              <option value="all">{t("Every category")}</option>
              {desk.byCategory.map((c) => (
                <option key={c.key} value={c.key}>
                  {`${t(c.label)} (${c.lines})`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("Store")}>
            <Select value={location} onChange={(e) => setLocation(e.target.value)} aria-label={t("Store")}>
              <option value="all">{t("Every store")}</option>
              {desk.byLocation.map((l) => (
                <option key={l.code} value={l.code}>
                  {`${l.name} (${l.lines})`}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {register.items.length === 0 ? (
          /* build 7: an empty store says so, rather than "no match" over a filter nobody set */
          <EmptyState>
            <span data-testid="stores-empty">{t("Nothing has been entered in the store yet.")}</span>
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState>{t("No line matches that.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Line")}</th>
                <th>{t("State")}</th>
                <th className={s.num}>{t("On hand")}</th>
                <th className={s.num}>{t("Reserved")}</th>
                <th className={s.num}>{t("Available")}</th>
                <th className={s.num}>{t("On order")}</th>
                <th className={s.num}>{t("Cover")}</th>
                <th className={s.num}>{t("Value")}</th>
              </tr>
            </thead>
            <tbody data-testid="stores-catalogue">
              {shown.map((r) => (
                <Row key={r.item.sku} row={r} currency={register.currency} register={register} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <CategoriesCard register={register} who={session.principal.name} onApply={apply} />

      <Card
        collapsible title="What this desk cannot tell you" sub="Known limits of these figures">
        <ul className={ex.plain}>
          {desk.gaps.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
        <Refs
          wide
          list={[{ kind: "entered", ref: "stores register", label: t("the stores register"), enteredBy: desk.enteredBy }]}
        />
      </Card>

      <AskCard
        title={t("Ask about the stores")}
        sub={t("Answers come from the register only")}
        material={materialForStores(desk)}
        figures={desk.figures}
        own={(q) => answerStores(q, desk)}
        prompt={STORES_MODEL_PROMPT}
        placeholder={t("What is stopping a job?")}
        board={desk.boardLine}
      />

      {intake === "load" && (
        <IntakeDialog
          register={register}
          who={session.principal.name}
          onClose={() => setIntake(null)}
          onApply={apply}
        />
      )}
      {intake === "line" && (
        <NewLineDialog
          register={register}
          who={session.principal.name}
          onClose={() => setIntake(null)}
          onApply={apply}
        />
      )}
      {intake === "direct" && (
        <DeliveryDialog
          register={register}
          who={session.principal.name}
          onClose={() => setIntake(null)}
          onApply={apply}
        />
      )}
      {intake === "external" && (
        <ExternalOrderDialog
          register={register}
          who={session.principal.name}
          onClose={() => setIntake(null)}
          onApply={apply}
        />
      )}

      {receiving && (
        /* the same dialog, narrowed to an order's own outstanding lines */
        <DeliveryDialog
          register={register}
          orderNumber={receiving}
          who={session.principal.name}
          onClose={() => setReceiving(null)}
          onApply={apply}
        />
      )}
    </Page>
  );
}

function Row({ row, currency, register }: { row: StockRow; currency: string; register: StoresRegister }) {
  const { item } = row;
  return (
    <tr>
      <td className={s.name}>
        <Link to={`/stores/${encodeURIComponent(item.sku)}`}>{item.name}</Link>
        {item.critical && <span className={s.critical} title={item.critical.why}>{t("CRITICAL")}</span>}
        <div className={s.sku}>
          {item.sku} · {t(categoryLabel(item.category, register))}
        </div>
      </td>
      <td>
        <span className={s.state} data-state={row.state}>
          {t(STATE_LABEL[row.state])}
        </span>
        {row.obsolete && <div className={s.why}>{t("nothing drawn in a year")}</div>}
      </td>
      <td className={s.num}>{`${row.onHand} ${item.uom}`}</td>
      <td className={s.num}>
        {row.reserved > 0 ? (
          <span title={row.waiting.join(", ")}>{row.reserved}</span>
        ) : (
          <span className={s.unknown}>—</span>
        )}
      </td>
      <td className={`${s.num} ${row.available <= 0 ? s.bad : ""}`}>{row.available}</td>
      <td className={s.num}>
        {row.onOrder > 0 ? (
          <>
            {row.onOrder}
            {row.expectedAt && <div className={s.why}>{dueText(row.expectedAt)}</div>}
          </>
        ) : (
          <span className={s.unknown}>—</span>
        )}
      </td>
      <td className={s.num}>
        {row.cover === null ? (
          <span className={s.unknown} title={t("nothing has been drawn, so there is no rate to divide by")}>
            {t("unknown")}
          </span>
        ) : (
          tn(Math.round(row.cover), "{n} day", "{n} days")
        )}
      </td>
      <td className={s.num}>
        {row.value === null ? (
          <span className={s.unknown}>{t("no cost")}</span>
        ) : (
          `${Math.round(row.value).toLocaleString()} ${currency}`
        )}
      </td>
    </tr>
  );
}

/** "due in 6 days", "4 days late" — never a bare date the reader has to subtract from today. */
export function dueText(date: string, now = new Date()): string {
  const days = daysUntil(date, now);
  if (days === null) return date;
  if (days < 0) return tn(Math.abs(days), "{n} day late", "{n} days late");
  if (days === 0) return t("due today");
  return tn(days, "due in {n} day", "due in {n} days");
}

export { STORE_CATEGORIES };
